/*
 * Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export type SiteLocale = 'en-US' | 'zh-CN' | 'zh-HK';

/** Accept historical stored aliases, but ignore unsupported preferences. */
export function parseLocale(value: unknown): SiteLocale | undefined {
  if (typeof value !== 'string' || !value.trim()) {
    return undefined;
  }
  const locale = value.trim().replace(/_/g, '-').toLowerCase();
  if (/^en(?:-|$)/.test(locale)) {
    return 'en-US';
  }
  if (/^zh(?:-|$)/.test(locale)) {
    return /(?:hant|^zh-(?:hk|mo|tw))/.test(locale) ? 'zh-HK' : 'zh-CN';
  }
  return undefined;
}

export function getStoredLocale(): SiteLocale | undefined {
  // Keep Studio's historical localStorage priority over legacy cookies.
  try {
    const saved = parseLocale(localStorage.getItem('i18next'));
    if (saved) {
      return saved;
    }
  } catch (error) {
    // Storage can be blocked by browser policy.
    void error;
  }
  if (typeof document !== 'undefined') {
    for (const name of ['locale', 'i18next']) {
      const value = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .find(cookie => cookie.startsWith(`${name}=`))
        ?.slice(name.length + 1);
      try {
        const saved = parseLocale(value && decodeURIComponent(value));
        if (saved) {
          return saved;
        }
      } catch (error) {
        // Ignore malformed cookies, without overwriting them.
        void error;
      }
    }
  }
  return undefined;
}

export function resolveLocale(
  profileLocale?: unknown,
  defaultLocale?: unknown,
): SiteLocale {
  return (
    getStoredLocale() ??
    parseLocale(profileLocale) ??
    parseLocale(defaultLocale) ??
    'en-US'
  );
}

export function toI18nLanguage(locale: SiteLocale): 'en' | 'zh-CN' | 'zh-HK' {
  return locale === 'en-US' ? 'en' : locale;
}
